/** @format */

import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaClock } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import usePermissions from "../../../permissions/permission";
import { FaRupeeSign } from "react-icons/fa6";
import Cookies from "js-cookie";
import Select from "react-select"; // Import react-select
import axios from "axios"; // Ensure axios is imported
import { motion } from "framer-motion";

const LoanManagement = () => {
  const token = Cookies.get("user_token"); // Get auth token
  const [loanTypes, setLoanTypes] = useState([]);
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // New states for organization filter
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrganizationFilter, setSelectedOrganizationFilter] = useState(null); // null means "All" initially

  // Fetch Organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const res = await axios.get(`${API_URL}/v1/hris/organizations/organization`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.data.success && Array.isArray(res.data.data)) {
          setOrganizationOptions(res.data.data.map(org => ({
            value: org.id,
            label: org.organization_name
          })));
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        toast.error('Failed to load organizations');
        setOrganizationOptions([]); // Ensure options are cleared on error
      }
    };
    fetchOrganizations();
  }, [API_URL, token]);

  // Fetch Loan Types (now dependent on selectedOrganizationFilter)
  useEffect(() => {
    const fetchLoanTypes = async () => {
      try {
        const params = {};
        if (selectedOrganizationFilter) {
          params.organization_id = selectedOrganizationFilter.value; // Add _i if selected
        }

        const res = await axios.get(`${API_URL}/v1/hris/organizations/loan-types`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        // Ensure options are always objects for react-select
        const options = res.data.map((item) => ({ value: item.name, label: item.name, id: item.id, employment: item.employment, pendingCount: item.pendingCount }));
        setLoanTypes(options);
      } catch (error) {
        console.error('Error fetching loan types:', error);
        toast.error('Failed to load loan types');
        setLoanTypes([]); // Fallback to empty array
      }
    };

    fetchLoanTypes();
  }, [API_URL, token, selectedOrganizationFilter]); // Re-fetch when organization filter changes

  const handleViewClick = (id, name, employment) => {
    if (employment === "No") {
      toast.error("No Employees are found");
    } else {
      navigate("/loan-component", { state: { id, name } });
    }
  };

  // Color schemes for different cards
  const gradients = [
    { bg: "from-blue-500 to-blue-600", light: "bg-blue-50", border: "border-blue-500" },
    { bg: "from-purple-500 to-purple-600", light: "bg-purple-50", border: "border-purple-500" },
    { bg: "from-teal-500 to-teal-600", light: "bg-teal-50", border: "border-teal-500" },
    { bg: "from-pink-500 to-pink-600", light: "bg-pink-50", border: "border-pink-500" },
    { bg: "from-orange-500 to-orange-600", light: "bg-orange-50", border: "border-orange-500" },
    { bg: "from-indigo-500 to-indigo-600", light: "bg-indigo-50", border: "border-indigo-500" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="min-h-screen  font-montserrat">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-[25px]  mb-3 flex items-center gap-3">
            Loan Management
          </h1>
          <p className="text-gray-600 text-lg">Manage employee loan requests and approvals</p>
        </div>

        {/* Organization Filter */}
        <div className="mb-6 w-72">
          <label className="block text-gray-700 text-sm font-medium mb-2">
            Filter by Organization
          </label>
          <Select
            options={organizationOptions}
            value={selectedOrganizationFilter}
            onChange={(option) => setSelectedOrganizationFilter(option)}
            isClearable
            placeholder="Select an Organization"
          />
        </div>

        {/* Loan Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {loanTypes.map((loan, index) => {
            const colorScheme = gradients[index % gradients.length];
            return (
              <div
                key={loan.id} // Use unique ID from loan object
                className="group relative overflow-hidden rounded-3xl bg-white shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer"
                onClick={() => handleViewClick(loan.id, loan.label, loan.employment)} // Use loan.label for name
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${colorScheme.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

                {/* Animated Background Circles */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-200 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-purple-200 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>

                {/* Content */}
                <div className="relative p-6 z-10">
                  {/* Icon Section */}
                  <div className="mb-6 flex items-center justify-between">
                    <div className="relative">
                      <div className={`absolute inset-0 bg-gradient-to-br ${colorScheme.bg} rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity`}></div>
                      <div className={`relative bg-gradient-to-br ${colorScheme.bg} p-4 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <FaRupeeSign className="text-white text-3xl" />
                      </div>
                    </div>

                    {/* Arrow Icon */}
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                      <svg className="w-6 h-6 text-gray-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-800 group-hover:text-white transition-colors duration-300 mb-4">
                    {loan.label} {/* Use loan.label for display */}
                  </h3>

                  {/* Stats */}
                  <div className="mb-6 p-4 bg-gray-50 group-hover:bg-white/20 rounded-xl transition-colors duration-300">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-gray-800 group-hover:text-white transition-colors duration-300">
                        {loan.pendingCount || 0}
                      </span>
                      <span className="text-sm text-gray-500 group-hover:text-white/80 transition-colors duration-300 font-medium">
                        Pending Requests
                      </span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mb-4">
                    {loan.employment === "Yes" ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200 group-hover:bg-white/20 group-hover:text-white group-hover:border-white/30 transition-all">
                        <FaCheckCircle className="w-3.5 h-3.5" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 group-hover:bg-white/20 group-hover:text-white group-hover:border-white/30 transition-all">
                        <FaClock className="w-3.5 h-3.5" />
                        No Employees
                      </span>
                    )}
                  </div>

                  {/* Button */}
                  <button
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 group-hover:from-white group-hover:to-white text-white group-hover:text-blue-600 font-bold rounded-xl px-6 py-3 transition-all duration-300 shadow-lg group-hover:shadow-2xl flex items-center justify-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewClick(loan.id, loan.label, loan.employment);
                    }}
                  >
                    <span>View Details</span>
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {loanTypes.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <FaRupeeSign className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No loan types available</p>
            <p className="text-gray-400 text-sm mt-2">Add loan types to get started</p>
          </div>
        )}
      </div>
    </motion.div>

  );
};

export default LoanManagement;